// Thermal (ESC/POS) printing over WebUSB / Web Bluetooth. Falls back to browser print elsewhere.
const KEY = "rsmart_printer_cfg";
export const getPrinterConfig = () => { try { return { type: "usb", width: 58, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return { type: "usb", width: 58 }; } };
export const savePrinterConfig = (c) => localStorage.setItem(KEY, JSON.stringify(c));

let printer = null;
export const isPrinterConnected = () => !!printer;
export const disconnectPrinter = () => { try { printer?.close?.(); } catch (e) { console.warn("printer close failed", e); } printer = null; };

export async function connectPrinter(type) {
  return type === "bluetooth" ? connectBT() : connectUSB();
}

async function connectUSB() {
  if (!navigator.usb) throw new Error("WebUSB not supported — use Chrome on desktop");
  const device = await navigator.usb.requestDevice({ filters: [] });
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  let ifaceNum = 0, epOut = 1;
  for (const iface of device.configuration.interfaces) {
    const alt = iface.alternates[0];
    const out = alt.endpoints.find((e) => e.direction === "out");
    if (out) { ifaceNum = iface.interfaceNumber; epOut = out.endpointNumber; break; }
  }
  await device.claimInterface(ifaceNum);
  printer = { device, write: async (bytes) => { const CH = 4096; for (let i = 0; i < bytes.length; i += CH) await device.transferOut(epOut, bytes.slice(i, i + CH)); }, close: () => device.close() };
  return true;
}

async function connectBT() {
  if (!navigator.bluetooth) throw new Error("Web Bluetooth not supported — use Chrome");
  const SVC = ["000018f0-0000-1000-8000-00805f9b34fb", "0000ff00-0000-1000-8000-00805f9b34fb", "0000ffe0-0000-1000-8000-00805f9b34fb", "49535343-fe7d-4ae5-8fa9-9fafd205e455"];
  const device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: SVC });
  const gatt = await device.gatt.connect();
  let ch = null;
  for (const s of await gatt.getPrimaryServices()) {
    for (const c of await s.getCharacteristics()) { if (c.properties.write || c.properties.writeWithoutResponse) { ch = c; break; } }
    if (ch) break;
  }
  if (!ch) throw new Error("No writable characteristic found on printer");
  printer = { device, write: async (bytes) => { const CH = 180; for (let i = 0; i < bytes.length; i += CH) { const chunk = bytes.slice(i, i + CH); if (ch.properties.writeWithoutResponse) await ch.writeValueWithoutResponse(chunk); else await ch.writeValue(chunk); await new Promise((r) => setTimeout(r, 18)); } }, close: () => gatt.disconnect() };
  return true;
}

const enc = (s) => new TextEncoder().encode(s);
const b = (a) => Uint8Array.from(a);
const concat = (arrs) => { const len = arrs.reduce((a, x) => a + x.length, 0); const out = new Uint8Array(len); let o = 0; for (const a of arrs) { out.set(a, o); o += a.length; } return out; };
const ESC = { init: [0x1b, 0x40], left: [0x1b, 0x61, 0], center: [0x1b, 0x61, 1], boldOn: [0x1b, 0x45, 1], boldOff: [0x1b, 0x45, 0], dbl: [0x1d, 0x21, 0x11], normal: [0x1d, 0x21, 0x00], cut: [0x1d, 0x56, 0x42, 0x00] };
const money = (n) => "Rs" + Number(n || 0).toFixed(2);
function row(l, r, w) { l = String(l); r = String(r); if (l.length + r.length > w) l = l.slice(0, Math.max(0, w - r.length - 1)); return l + " ".repeat(Math.max(1, w - l.length - r.length)) + r + "\n"; }

export function buildReceipt(order, biz, width = 58) {
  const W = width >= 80 ? 48 : 32;
  const p = [];
  const line = (s) => p.push(enc(s));
  p.push(b(ESC.init), b(ESC.center), b(ESC.dbl), b(ESC.boldOn)); line((biz?.name || "Rani Super Market") + "\n"); p.push(b(ESC.normal), b(ESC.boldOff));
  if (biz?.address) line(biz.address + (biz.city ? ", " + biz.city : "") + "\n");
  if (biz?.phone) line("Ph: " + biz.phone + "\n");
  if (biz?.show_gst && biz?.gstin) line("GSTIN: " + biz.gstin + "\n");
  p.push(b(ESC.left)); line("-".repeat(W) + "\n");
  line("Invoice: " + (order.invoice_no || order.order_no) + "\n");
  line(new Date(order.created_at).toLocaleString("en-IN") + "\n");
  if (order.customer_name && order.customer_name !== "Walk-in") line("Cust: " + order.customer_name + (order.customer_phone ? " " + order.customer_phone : "") + "\n");
  line("-".repeat(W) + "\n");
  (order.items || []).forEach((it) => { line(String(it.name).slice(0, W) + "\n"); line(row(`  ${it.quantity} x ${money(it.price)}` + (it.discount ? ` -${money(it.discount)}` : ""), money(it.line_total), W)); });
  line("-".repeat(W) + "\n");
  line(row("Subtotal", money(order.subtotal), W));
  if (order.discount > 0) line(row("Discount", "-" + money(order.discount), W));
  line(row("GST", money(order.gst), W));
  if (order.extra_charge > 0) line(row("Charges", money(order.extra_charge), W));
  if (order.round_off) line(row("Round off", money(order.round_off), W));
  p.push(b(ESC.boldOn)); line(row("TOTAL", money(order.total), W)); p.push(b(ESC.boldOff));
  line("-".repeat(W) + "\n");
  (order.payments || []).forEach((pay) => line(row(String(pay.method).toUpperCase(), money(pay.amount), W)));
  if (order.change > 0) line(row("Change", money(order.change), W));
  if (order.credit_amount > 0) line(row("Udhaar due", money(order.credit_amount), W));
  line("-".repeat(W) + "\n");
  p.push(b(ESC.center)); line((biz?.footer_note || "Thank you! Visit again.") + "\n\n\n");
  p.push(b(ESC.cut));
  return concat(p);
}

export async function printReceipt(order, biz, width) {
  if (!printer) throw new Error("No printer connected");
  await printer.write(buildReceipt(order, biz, width || getPrinterConfig().width));
}
